import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import MarkdownRenderer from '../components/MarkdownRenderer';
import ImageUpload from '../components/ImageUpload';
import './PostDetail.css';

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const editTextareaRef = useRef(null);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    tags: []
  });

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`/api/posts/${id}`);
      setPost(response.data);
      setEditForm({
        title: response.data.title,
        content: response.data.content,
        tags: response.data.tags
      });
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch post');
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (!isAdmin) {
      alert('只有管理员才能编辑文章');
      return;
    }
    setIsEditing(true);
    setShowPreview(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setShowPreview(false);
    setEditForm({
      title: post.title,
      content: post.content,
      tags: post.tags
    });
  };

  const handleSaveEdit = async () => {
    if (!isAdmin) {
      alert('只有管理员才能编辑文章');
      return;
    }

    try {
      const response = await axios.put(`/api/posts/${id}`, editForm);
      setPost(response.data);
      setIsEditing(false);
      setShowPreview(false);
    } catch (err) {
      if (err.response?.status === 401) {
        alert('权限不足，请先登录管理员账户');
      } else {
        setError('Failed to update post');
      }
    }
  };

  const handleDelete = async () => {
    if (!isAdmin) {
      alert('只有管理员才能删除文章');
      return;
    }

    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await axios.delete(`/api/posts/${id}`);
        navigate('/');
      } catch (err) {
        if (err.response?.status === 401) {
          alert('权限不足，请先登录管理员账户');
        } else {
          setError('Failed to delete post');
        }
      }
    }
  };

  const handleTagsChange = (e) => {
    const tagsString = e.target.value;
    const tagsArray = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
    setEditForm({
      ...editForm,
      tags: tagsArray
    });
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  const handleImageUpload = (imageMarkdown) => {
    if (!isAdmin) {
      alert('只有管理员才能上传图片');
      return;
    }

    // 在光标位置插入图片Markdown
    const textarea = editTextareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const content = editForm.content;
    const newContent = content.substring(0, start) + '\n' + imageMarkdown + '\n' + content.substring(end);
    
    setEditForm({
      ...editForm,
      content: newContent
    });
    
    // 设置光标位置到插入的图片后面
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + imageMarkdown.length + 2, start + imageMarkdown.length + 2);
    }, 0);
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!post) return <div className="error">Post not found</div>;

  return (
    <div className="post-detail">
      <div className="container">
        <div className="post-navigation">
          <Link to="/" className="back-link">← 返回首页</Link>
        </div>

        <article className="post-content">
          {isEditing && isAdmin ? (
            <div className="edit-form">
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                className="edit-title"
                placeholder="文章标题"
              />
              
              <div className="edit-toolbar">
                <button 
                  type="button"
                  onClick={togglePreview}
                  className={`btn btn-outline ${showPreview ? 'active' : ''}`}
                >
                  {showPreview ? '编辑模式' : 'Markdown 预览'}
                </button>
                <div className="markdown-hint">
                  支持 Markdown 格式: **粗体** *斜体* `代码` [链接](url) # 标题 ![图片](url)
                </div>
              </div>

              <div className="image-style-hints">
                <strong>图片样式语法:</strong>
                <span>![描述|size:small](url) - 小图</span>
                <span>![描述|width:300px](url) - 自定义宽度</span>
                <span>![描述|radius:10px](url) - 圆角</span>
                <span>![描述|round](url) - 圆形</span>
                <span>![描述|shadow](url) - 阴影</span>
                <span>![描述|size:medium|round|shadow](url) - 组合样式</span>
              </div>

              {/* 图片上传组件 - 启用粘贴功能 */}
              <ImageUpload 
                onImageUpload={handleImageUpload} 
                enablePaste={true}
                targetElementRef={editTextareaRef}
              />

              <div className="edit-content-wrapper">
                {showPreview ? (
                  <div className="preview-content">
                    <h3>预览效果:</h3>
                    <MarkdownRenderer content={editForm.content} />
                  </div>
                ) : (
                  <textarea
                    ref={editTextareaRef}
                    value={editForm.content}
                    onChange={(e) => setEditForm({...editForm, content: e.target.value})}
                    className="edit-content"
                    placeholder="请输入文章内容，支持 Markdown 格式&#10;&#10;示例：&#10;# 一级标题&#10;## 二级标题&#10;**粗体文本**&#10;*斜体文本*&#10;`代码片段`&#10;```javascript&#10;代码块&#10;```&#10;> 引用文本&#10;- 列表项目&#10;&#10;图片：![alt文本](图片URL)&#10;&#10;🎨 图片样式示例：&#10;![小图|size:small](图片URL)&#10;![中等大小圆形图片|size:medium|round](图片URL)&#10;![自定义宽度带阴影|width:400px|shadow](图片URL)&#10;![圆角图片|radius:15px](图片URL)&#10;&#10;💡 提示：在此编辑区域按 Ctrl+V 可直接粘贴剪贴板中的图片"
                    rows="15"
                  />
                )}
              </div>

              <input
                type="text"
                value={editForm.tags.join(', ')}
                onChange={handleTagsChange}
                className="edit-tags"
                placeholder="标签 (用逗号分隔)"
              />
              <div className="edit-actions">
                <button onClick={handleSaveEdit} className="btn btn-primary">
                  保存
                </button>
                <button onClick={handleCancelEdit} className="btn btn-outline">
                  取消
                </button>
              </div>
            </div>
          ) : (
            <>
              <header className="post-header">
                <h1 className="post-title">{post.title}</h1>
                <div className="post-meta">
                  <span className="author">作者: {post.author}</span>
                  <span className="date">{post.created_at}</span>
                  <span className="view-count">阅读量: {post.view_count || 0}</span>
                </div>
                <div className="post-tags">
                  {post.tags.map((tag, index) => (
                    <span key={index} className="tag">#{tag}</span>
                  ))}
                </div>
              </header>

              <div className="post-body">
                <MarkdownRenderer content={post.content} />
              </div>

              {/* 只有管理员才能看到编辑和删除按钮 */}
              {isAdmin && (
                <div className="post-actions">
                  <button onClick={handleEdit} className="btn btn-primary">
                    编辑
                  </button>
                  <button onClick={handleDelete} className="btn btn-danger">
                    删除
                  </button>
                </div>
              )}
            </>
          )}
        </article>
      </div>
    </div>
  );
};

export default PostDetail;